export type EvaluationLevel = 1 | 2 | 3 | 4 | 5;
export type CommunicationLevel = 'Beginner' | 'Intermediate' | 'Advanced' | 'Native-like';
export type DominantRegister = 'formal' | 'informal' | 'mixed';
export type ConversationType = 'casual' | 'academic' | 'professional' | 'social';

export interface OverallScore {
  rating: number;
  level: EvaluationLevel;
}

export interface Assessment {
  score: number;
  level: EvaluationLevel | CommunicationLevel;
  feedback: string;
  suggestions: string[];
}

export interface Assessments {
  grammar: Assessment;
  vocabulary: Assessment;
  communication_effectiveness: Assessment;
  pragmatic_competence: Assessment;
}

export interface ConversationAnalysis {
  dominant_register: DominantRegister;
  conversation_type: ConversationType;
  key_observations: string[];
  notable_patterns: string[];
}

export interface ActionableFeedback {
  immediate_focus_areas: string[];
  practice_recommendations: string[];
  next_steps: string[];
}

export interface PerformanceEvaluation {
  overall_score: OverallScore;
  assessments: Assessments;
  conversation_analysis: ConversationAnalysis;
  actionable_feedback: ActionableFeedback;
}
export const performanceAnalysisPromptTemplate = (language: string): string => `
You are an expert ${language} language evaluator. Analyze the following conversation and provide a detailed assessment focusing on these four key areas. 
Return your analysis in the exact JSON format specified below.

Assessment Categories & Evaluation Rules:
1. GRAMMAR
Evaluate the user's grammatical competence based on:

Sentence structure accuracy: Proper use of simple, compound, and complex sentences
Verb tense consistency: Correct past, present, future tense usage and sequence
Subject-verb agreement: Proper matching of subjects with verbs
Article usage: Correct use of a, an, the, and zero articles
Preposition accuracy: Proper use of prepositions (in, on, at, by, etc.)
Pronoun reference: Clear and correct pronoun usage
Question formation: Proper structure of questions and question tags
Conditional structures: Correct use of if-clauses and conditional sentences
Passive voice: Appropriate use when needed
Modal verbs: Correct usage of can, could, should, would, must, etc.

2. VOCABULARY
Evaluate the user's vocabulary proficiency based on:

Range of vocabulary: Breadth across different topics and domains
Precise word choice: Selecting the most appropriate word for context
Nuanced meanings: Understanding subtle differences between similar words
Collocations: Natural word combinations (e.g., "strong coffee", "heavy rain")
Idiomatic expressions: Use of phrases, metaphors, and cultural expressions
Academic vs. conversational vocabulary: Appropriate register switching
Synonyms and variety: Avoiding repetition, using diverse expressions
Word formation: Understanding prefixes, suffixes, and word families
Context-appropriate usage: Using words correctly in their proper contexts
Frequency of vocabulary: Using both common and less common words appropriately

3. COMMUNICATION EFFECTIVENESS
Evaluate the user's ability to communicate clearly and effectively based on:

Clarity of message delivery: Can the listener easily understand the intended meaning?
Coherence and organization: Logical flow of ideas and smooth transitions
Appropriate register: Matching formality level to the situation and audience
Turn-taking skills: Proper conversational management and participation
Repair strategies: Ability to clarify when misunderstood
Engagement level: Active participation and responsiveness
Topic development: How well ideas are expanded and elaborated
Persuasion and argumentation: Logical reasoning and evidence presentation
Narrative ability: Storytelling coherence and engagement
Functional language use: Requests, apologies, complaints, compliments, etc.

4. PRAGMATIC COMPETENCE
Evaluate the user's cultural and social language awareness based on:

Social norms understanding: Awareness of appropriate language for different contexts
Politeness markers: Use of please, thank you, excuse me, and other courtesy expressions
Cultural references: Understanding and using culturally appropriate expressions
Audience adaptation: Adjusting language style for different listeners
Implied meaning recognition: Understanding subtext, sarcasm, and indirect communication
Speech acts: Appropriate use of requests, invitations, refusals, and suggestions
Conversational conventions: Following cultural norms for conversation flow
Humor and informal language: Appropriate use of jokes, slang, and casual expressions
Power dynamics awareness: Using appropriate language with different social statuses
Contextual sensitivity: Adapting language to formal vs. informal situations

Required JSON Response Format:
json{
  "overall_score": {
    "rating": 0-100,
    "level": "1|2|3|4|5"
  },
  "assessments": {
    "grammar": {
      "score": 0-100,
      "level": "1|2|3|4|5",
      "feedback": "Comprehensive feedback covering strengths, weaknesses, specific observations, and examples from the conversation that demonstrate grammatical competence or areas for improvement",
      "suggestions": [
        "Grammar rule 1 to study (e.g., 'Present Perfect vs Simple Past')",
        "Grammar concept 2 to practice (e.g., 'Conditional sentences')",
        "Grammar topic 3 to review (e.g., 'Article usage with countable/uncountable nouns')"
      ]
    },
    "vocabulary": {
      "score": 0-100,
      "level": "1|2|3|4|5",
      "feedback": "Comprehensive feedback covering vocabulary range, precision, strengths, weaknesses, and specific examples from the conversation showing vocabulary usage",
      "suggestions": [
        "Word 1 to learn (e.g., 'comprehensive')",
        "Word 2 to practice (e.g., 'elaborate')",
        "Word 3 to add to vocabulary (e.g., 'substantial')",
        "Phrase 1 to learn (e.g., 'take into account')",
        "Collocation 1 to practice (e.g., 'make progress')"
      ]
    },
    "communication_effectiveness": {
      "score": 0-100,
      "level": "Beginner|Intermediate|Advanced|Native-like",
      "feedback": "Comprehensive feedback about clarity, coherence, register appropriateness, and overall communication skills with specific examples from the conversation",
      "suggestions": [
        "Communication skill 1 to practice (e.g., 'Using transition words')",
        "Strategy 1 to develop (e.g., 'Asking clarifying questions')",
        "Technique 1 to improve (e.g., 'Paraphrasing for clarity')"
      ]
    },
    "pragmatic_competence": {
      "score": 0-100,
      "level": "Beginner|Intermediate|Advanced|Native-like",
      "feedback": "Comprehensive feedback about cultural awareness, social appropriateness, politeness, and contextual language use with specific examples from the conversation",
      "suggestions": [
        "Cultural concept 1 to learn (e.g., 'Indirect refusal strategies')",
        "Social skill 1 to practice (e.g., 'Small talk conventions')",
        "Pragmatic area 1 to study (e.g., 'Formal email etiquette')"
      ]
    }
  },
  "conversation_analysis": {
    "dominant_register": "formal|informal|mixed",
    "conversation_type": "casual|academic|professional|social",
    "key_observations": ["observation1", "observation2", "observation3"],
    "notable_patterns": ["pattern1", "pattern2"]
  },
  "actionable_feedback": {
    "immediate_focus_areas": ["area1", "area2"],
    "practice_recommendations": ["recommendation1", "recommendation2", "recommendation3"],
    "next_steps": ["step1", "step2"]
  }
}
Scoring Guidelines:

0-20: Beginner - Basic understanding, frequent errors, limited range
20-40: Beginner Intermediate - Moderate competence, some errors, adequate range
40-60: Intermediate - Good competence, minor errors, good range
60-80: Intermediate Advanced - Exceptional competence, rare errors, sophisticated range
80-100: Advanced - Exceptional competence, rare errors, sophisticated range

Important Notes:

Be specific and constructive in your feedback
Use actual examples from the conversation provided
Focus on observable language behaviors only
Provide actionable improvement suggestions tailored to each category
Consider the context and purpose of the conversation
If multiple speakers are present, specify which speaker you're evaluating

`;
